import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, type AdminHealth, type HealthCheck } from './api'
import { formatUptime } from './format'

const GROUPS: { title: string; match: (key: string) => boolean }[] = [
  { title: 'Core', match: (key) => ['database', 'media', 'admin'].includes(key) },
  { title: 'Site', match: (key) => ['rendering', 'views', 'databaseSize', 'storage'].includes(key) },
  { title: 'Services', match: (key) => key === 'mailchimp' },
  { title: 'Domains & SSL', match: (key) => key.startsWith('domain:') },
]

function statusLabel(check: HealthCheck) {
  if (check.ok === null) return { text: 'Not set up', className: 'text-muted' }
  return check.ok ? { text: 'OK', className: 'text-muted' } : { text: 'Issue', className: 'text-rose' }
}

export default function AdminHealth() {
  const [health, setHealth] = useState<AdminHealth | null>(null)
  const [error, setError] = useState('')
  const [checking, setChecking] = useState(true)
  const [now, setNow] = useState(Date.now())

  const load = useCallback(() => {
    api<AdminHealth>('/api/admin/health')
      .then((data) => {
        setHealth(data)
        setError('')
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setChecking(false))
  }, [])

  useEffect(load, [load])

  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(tick)
  }, [])

  if (error && !health) return <p className="text-rose">{error}</p>
  if (!health) return <p className="text-muted">Running health checks…</p>

  const uptime = formatUptime(Math.floor((now - health.startedAt) / 1000))
  const problems = health.checks.filter((check) => check.ok === false).length

  return (
    <section>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-caps text-[10px] text-rose">Site</p>
          <h2 className="mt-1 font-serif text-[32px] leading-none font-medium">Health</h2>
        </div>
        <button
          type="button"
          onClick={() => {
            setChecking(true)
            load()
          }}
          disabled={checking}
          className="text-caps inline-flex h-9 items-center bg-ink px-4 text-white hover:bg-rose disabled:opacity-60"
        >
          {checking ? 'Checking…' : 'Check again'}
        </button>
      </div>
      <p className="mt-3 max-w-[56ch] text-sm text-muted">
        Live checks of the server, the site, Mailchimp and the domains. They run each time this page opens.
      </p>
      {error ? <p className="mt-4 text-sm text-rose">{error}</p> : null}

      <div className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="border border-rule px-4 py-5">
          <p className="text-caps text-[10px] text-muted">Status</p>
          <p className={`mt-2 font-serif text-[36px] leading-none ${health.ok ? 'text-ink' : 'text-rose'}`}>
            {health.ok ? 'Healthy' : 'Attention'}
          </p>
          <p className="mt-2 text-[12px] text-muted">
            {problems ? `${problems} ${problems === 1 ? 'check needs' : 'checks need'} a look` : 'All checks passed'}
          </p>
        </div>
        <div className="border border-rule px-4 py-5">
          <p className="text-caps text-[10px] text-muted">Uptime</p>
          <p className="mt-2 font-serif text-[36px] leading-none">{uptime}</p>
          <p className="mt-2 text-[12px] text-muted">
            Since {new Date(health.startedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </p>
        </div>
        <div className="min-w-0 border border-rule px-4 py-5">
          <p className="text-caps text-[10px] text-muted">Live version</p>
          <p className="mt-2 font-mono text-[28px] leading-none">{health.version.commit ?? 'Unknown'}</p>
          <p className="mt-2 truncate text-[12px] text-muted" title={health.version.message ?? undefined}>
            {health.version.message ?? 'Railway did not report a commit'}
            {health.version.branch ? ` · ${health.version.branch}` : ''}
          </p>
        </div>
      </div>

      {GROUPS.map((group) => {
        const checks = health.checks.filter((check) => group.match(check.key))
        if (!checks.length) return null
        return (
          <div key={group.title} className="mt-10">
            <h3 className="text-caps text-[11px] text-muted">{group.title}</h3>
            <ul className="mt-4 divide-y divide-rule border-y border-rule">
              {checks.map((check) => {
                const status = statusLabel(check)
                return (
                  <li key={check.key} className="grid gap-x-4 gap-y-0.5 py-2.5 text-sm sm:grid-cols-[11rem_5.5rem_1fr]">
                    <span>{check.label}</span>
                    <span className={`text-caps text-[10px] leading-5 ${status.className}`}>{status.text}</span>
                    <span className={`min-w-0 break-words ${check.ok === false ? 'text-rose' : 'text-copy'}`}>{check.detail}</span>
                  </li>
                )
              })}
            </ul>
          </div>
        )
      })}

      <p className="mt-10 text-sm text-muted">
        Activity and errors are on the{' '}
        <Link to="/admin/logs" className="text-ink underline underline-offset-2 hover:text-rose">
          Logs
        </Link>{' '}
        page.
      </p>
    </section>
  )
}
